'use client'

import { ApolloProvider } from '@apollo/client'
import { domAnimation, LazyMotion } from 'framer-motion'
import { usePathname } from 'next/navigation'
import { NuqsAdapter } from 'nuqs/adapters/next/app'
import type { ReactNode } from 'react'
import React, { lazy, Suspense, useMemo } from 'react'

import { ChatProvider } from '../contexts/chat-context'
import { useRemovePointerEvents } from '../hooks/use-remove-pointer-events'
import { client } from '../lib/apollo-client'
import { isLightThemeRoute } from '../utils/light-theme-routes'
import { ChainLoader } from './chain-loader'
import { DrawersContainer } from './drawers-container'
import { SentryInit } from './sentry-client'
import { ChainThemeProvider } from './theme-provider'

const AnalyticsProvider = lazy(() =>
  import('../contexts/analytics-context').then((module) => ({ default: module.AnalyticsProvider }))
)

const AnalyticsProviderWrapper = ({ children }: { children: ReactNode }) => (
  <Suspense fallback={<ChainLoader />}>
    <AnalyticsProvider>{children}</AnalyticsProvider>
  </Suspense>
)

export const Providers: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Memoize Apollo client to prevent unnecessary re-creations
  const apolloClient = useMemo(() => client, [])
  const pathname = usePathname()
  const isEmbedRoute = isLightThemeRoute(pathname)
  useRemovePointerEvents()

  return (
    <ChainThemeProvider
      attribute="class"
      defaultTheme="system"
      enableSystem={!isEmbedRoute}
      disableTransitionOnChange={true}
      forcedTheme={isEmbedRoute ? 'light' : undefined}
      storageKey="chaine-theme"
    >
      <ApolloProvider client={apolloClient}>
        <NuqsAdapter>
          <AnalyticsProviderWrapper>
            <ChatProvider>
              <LazyMotion features={domAnimation}>
                <DrawersContainer />
                <SentryInit />
                {children}
              </LazyMotion>
            </ChatProvider>
          </AnalyticsProviderWrapper>
        </NuqsAdapter>
      </ApolloProvider>
    </ChainThemeProvider>
  )
}
